import React, { Children, cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiMoreHorizontal, FiX } from 'react-icons/fi';
import useMobileSchedule from '../../../hooks/useMobileSchedule';
import './MobileJobActions.css';
import { lockScheduleScroll } from './lockScheduleScroll';

function actionItem(child, index, close) {
    if (!isValidElement(child)) return null;
    const label = child.props['aria-label'] || child.props.title || child.props.text;
    // Tooltips are hover controls; expose their text as the action label on touch screens.
    const action = child.props.text && isValidElement(child.props.children) ? child.props.children : child;
    if (isValidElement(action) && action.type === 'button') {
        return cloneElement(action, { key: index, onClick: event => { action.props.onClick?.(event); close(); }, className: `${action.props.className || ''} mobile-job-action` },
            action.props.children, label && <span>{label}</span>);
    }
    return <div className="mobile-job-action-custom" key={index}>{action}{label && <span>{label}</span>}</div>;
}

export default function MobileJobActions({ job, children, className, triggerClassName = '', sheetLayer = 190, ...desktopProps }) {
    const mobile = useMobileSchedule();
    const [open, setOpen] = useState(false);
    const titleId = useId();
    const sheet = useRef(null);
    const trigger = useRef(null);
    useEffect(() => {
        if (!open || !mobile) return;
        const unlock = lockScheduleScroll();
        const returnFocus = trigger.current;
        sheet.current?.focus();
        return () => { unlock(); returnFocus?.focus(); };
    }, [open, mobile]);
    if (!mobile) return <div className={className} {...desktopProps}>{children}</div>;
    const id = job.schedule_row_id || job.childUniqueId || job.unique_id || job.uniqueId;
    const customer = job.customer_name || job.customerName;
    const address = job.customer_address || job.customerAddress;
    const type = job.quote_info?.quote_type || job.quoteType;
    const scheduledDate = job.quote_info?.site_measure_date || job.quote_info?.expected_delivery_date || job.quote_info?.expected_finish_date;
    const status = job.quote_info?.design_status || job.quote_info?.delivery_status;
    return <>
        <button ref={trigger} type="button" aria-label={`Actions for ${id}`} aria-haspopup="dialog" aria-expanded={open}
            className={`mobile-job-actions-trigger ${triggerClassName}`} onPointerDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); setOpen(true); }}><FiMoreHorizontal /></button>
        {open && createPortal(<div className="mobile-job-sheet-backdrop" style={{ zIndex: sheetLayer }} onPointerDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) setOpen(false); }}>
            <section ref={sheet} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} className="mobile-job-sheet"
                onKeyDown={e => {
                    if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
                    if (e.key !== 'Tab') return;
                    const items = [...sheet.current.querySelectorAll('button:not(:disabled), a[href], input, select, textarea')];
                    const first = items[0], last = items[items.length - 1];
                    if (e.shiftKey && (document.activeElement === first || document.activeElement === sheet.current)) { e.preventDefault(); last?.focus(); }
                    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
                }}>
                <div className="mobile-job-sheet-handle" />
                <header><div><p className="mobile-job-sheet-eyebrow">Job actions</p><h2 id={titleId}>{id}</h2></div>
                    <button type="button" aria-label="Close job actions" onClick={() => setOpen(false)}><FiX /></button></header>
                <div className="mobile-job-sheet-details"><strong>{customer || 'No customer'}</strong><p>{address || 'No address provided'}</p>
                    {type && <span>{type.replace(/_/g, ' ')}</span>}
                    {status && <p>Status: {status.replace(/_/g, ' ')}</p>}
                    {scheduledDate && <p>Scheduled: {new Date(scheduledDate).toLocaleDateString('en-AU', { timeZone: 'UTC' })}</p>}
                    {job.quote_info?.site_measure_date && <p>Site measure: {job.quote_info.site_measure_confirmed ? 'Completed' : 'Awaiting completion'}</p>}</div>
                <div className="mobile-job-sheet-options">{Children.map(children, (child, index) => actionItem(child, index, () => setOpen(false)))}</div>
            </section>
        </div>, document.body)}
    </>;
}
